// Copyright (c) Said Borna. All rights reserved.
import { type ReactElement, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Calendar, FileText, GitBranch, PenLine, Bot, Sparkles,
    Brain, Lightbulb, Users, UserCircle, Image, MessageSquare,
    BarChart3, TrendingUp, Download, LayoutDashboard, Settings,
    LogOut, Plug, Layers, ListChecks, Settings2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { getDisplayName, isStaffRole } from '@/types';
import { cn } from '@/lib/utils';

interface SidebarLink {
    readonly label: string;
    readonly path: string;
    readonly icon: ReactElement;
}

interface SidebarGroup {
    readonly title: string;
    readonly links: readonly SidebarLink[];
}

const ICON_SIZE = 18;

const CLIENT_GROUPS: readonly SidebarGroup[] = [
    {
        title: 'OVERVIEW',
        links: [
            { label: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={ICON_SIZE} /> },
            { label: 'Calendar', path: '/calendar', icon: <Calendar size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'CONTENT',
        links: [
            { label: 'Content', path: '/content', icon: <FileText size={ICON_SIZE} /> },
            { label: 'Ideas', path: '/content-ideas', icon: <Lightbulb size={ICON_SIZE} /> },
            { label: 'Media', path: '/media-library', icon: <Image size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'ACCOUNT',
        links: [
            { label: 'Profile', path: '/profile', icon: <UserCircle size={ICON_SIZE} /> },
            { label: 'Settings', path: '/settings', icon: <Settings size={ICON_SIZE} /> },
        ],
    },
];

const STAFF_GROUPS: readonly SidebarGroup[] = [
    {
        title: 'CONTENT',
        links: [
            { label: 'Calendar', path: '/admin/calendar', icon: <Calendar size={ICON_SIZE} /> },
            { label: 'Content', path: '/admin/content-manager', icon: <FileText size={ICON_SIZE} /> },
            { label: 'Pipeline', path: '/admin/pipeline-manager', icon: <GitBranch size={ICON_SIZE} /> },
            { label: 'All Posts', path: '/admin/aggregated-pipeline', icon: <Layers size={ICON_SIZE} /> },
            { label: 'Create Post', path: '/admin/create-post', icon: <PenLine size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'AI',
        links: [
            { label: 'Brand Profile', path: '/admin/brand-profile', icon: <Sparkles size={ICON_SIZE} /> },
            { label: 'Best Practice Queue', path: '/admin/suggestions', icon: <ListChecks size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'MANAGE',
        links: [
            { label: 'Teams', path: '/admin/teams', icon: <Users size={ICON_SIZE} /> },
            { label: 'Users', path: '/admin/user-manager', icon: <UserCircle size={ICON_SIZE} /> },
            { label: 'Media', path: '/admin/media-management', icon: <Image size={ICON_SIZE} /> },
            { label: 'Ideas', path: '/admin/content-ideas', icon: <MessageSquare size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'OPERATIONS',
        links: [
            { label: 'Writer Performance', path: '/admin/writer-performance', icon: <BarChart3 size={ICON_SIZE} /> },
            { label: 'Intelligence', path: '/admin/content-intelligence', icon: <TrendingUp size={ICON_SIZE} /> },
            { label: 'Import History', path: '/admin/import-history', icon: <Download size={ICON_SIZE} /> },
            { label: 'Integrations', path: '/admin/integrations', icon: <Plug size={ICON_SIZE} /> },
        ],
    },
    {
        title: 'ADVANCED',
        links: [
            { label: 'AI Studio', path: '/admin/ai-studio', icon: <Bot size={ICON_SIZE} /> },
            { label: 'Write Brain', path: '/admin/write-brain', icon: <Brain size={ICON_SIZE} /> },
            { label: 'Inspiration', path: '/admin/inspiration', icon: <Lightbulb size={ICON_SIZE} /> },
            { label: 'Prompt Config', path: '/admin/prompt-config', icon: <Settings2 size={ICON_SIZE} /> },
        ],
    },
];

interface SidebarProps {
    readonly groups: readonly SidebarGroup[];
}

/**
 * Enterprise sidebar navigation.
 * Fixed left panel with grouped nav items, Lucide icons, and active state indicators.
 */
function Sidebar({ groups }: SidebarProps): ReactElement {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();

    const handleSignOut = async (): Promise<void> => {
        await signOut();
        navigate('/', { replace: true });
    };

    return (
        <aside className="flex h-screen w-60 flex-col border-r border-border bg-white">
            {/* Logo */}
            <div className="flex h-16 items-center gap-2.5 border-b border-border px-5">
                <span className="text-xl font-bold tracking-tight text-foreground">BAM 2.0</span>
                {user && isStaffRole(user.role) && (
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Staff
                    </span>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-4">
                {groups.map((group, groupIdx) => (
                    <div key={group.title} className={cn(groupIdx > 0 && 'mt-6')}>
                        <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-widest text-[#9CA3AF]">
                            {group.title}
                        </p>
                        <ul className="space-y-0.5">
                            {group.links.map((link) => (
                                <li key={link.path}>
                                    <NavLink
                                        to={link.path}
                                        className={({ isActive }) =>
                                            cn(
                                                'flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition-colors duration-150',
                                                isActive
                                                    ? 'border-l-[3px] border-foreground bg-[#F3F4F6] pl-[5px] text-foreground'
                                                    : 'text-[#374151] hover:bg-[#F9FAFB]',
                                            )
                                        }
                                    >
                                        <span className={cn('flex-shrink-0 text-[#6B7280]')}>
                                            {link.icon}
                                        </span>
                                        {link.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            {/* User section */}
            {user && (
                <div className="border-t border-border p-4">
                    <p className="text-sm font-semibold text-foreground">
                        {getDisplayName({ full_name: user.fullName, email: user.email })}
                    </p>
                    <p className="mt-0.5 text-xs capitalize text-muted-foreground">
                        {user.role.replace('_', ' ')}
                    </p>
                    <button
                        onClick={handleSignOut}
                        className="mt-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <LogOut size={14} />
                        Sign out
                    </button>
                </div>
            )}
        </aside>
    );
}

interface AppLayoutProps {
    readonly children: ReactNode;
}

/**
 * Main layout for client users.
 * Sidebar + content area.
 */
export function AppLayout({ children }: AppLayoutProps): ReactElement {
    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar groups={CLIENT_GROUPS} />
            <MainContent>{children}</MainContent>
        </div>
    );
}

/**
 * Main layout for staff users.
 * Sidebar with admin links + content area + impersonation banner.
 */
export function AdminLayout({ children }: AppLayoutProps): ReactElement {
    const { isImpersonating, impersonatedUserName, stopImpersonation, minutesRemaining } = useImpersonation();

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar groups={STAFF_GROUPS} />
            <div className="flex flex-1 flex-col overflow-hidden">
                {isImpersonating && (
                    <ImpersonationBanner
                        userName={impersonatedUserName ?? 'Unknown'}
                        minutesRemaining={minutesRemaining}
                        onStop={stopImpersonation}
                    />
                )}
                <MainContent>{children}</MainContent>
            </div>
        </div>
    );
}

interface MainContentProps {
    readonly children: ReactNode;
}

/**
 * Scrollable main content area with enterprise spacing.
 */
function MainContent({ children }: MainContentProps): ReactElement {
    return (
        <main className="flex-1 overflow-y-auto bg-[#FAFAFA] p-8">
            {children}
        </main>
    );
}

interface ImpersonationBannerProps {
    readonly userName: string;
    readonly minutesRemaining: number | null;
    readonly onStop: () => void;
}

/**
 * Warning banner shown when staff is impersonating a client.
 */
function ImpersonationBanner({ userName, minutesRemaining, onStop }: ImpersonationBannerProps): ReactElement {
    return (
        <div className="flex items-center justify-between bg-[#F97316] px-4 py-2 text-sm font-medium text-white">
            <div className="flex items-center gap-3">
                <span>Viewing as: {userName}</span>
                {minutesRemaining !== null && (
                    <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium">
                        {minutesRemaining} min remaining
                    </span>
                )}
            </div>
            <button
                onClick={onStop}
                className="rounded-lg bg-white/20 px-3 py-1 text-xs font-medium transition-colors hover:bg-white/30"
            >
                Exit impersonation
            </button>
        </div>
    );
}

interface PageLayoutProps {
    readonly title: string;
    readonly description?: string;
    readonly actions?: ReactNode;
    readonly children: ReactNode;
}

/**
 * Standard page layout with title, optional description, and action buttons.
 * Enterprise typography: 24px semibold titles, 14px regular description.
 */
export function PageLayout({ title, description, actions, children }: PageLayoutProps): ReactElement {
    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
                    {description && (
                        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                    )}
                </div>
                {actions && <div className="flex items-center gap-2">{actions}</div>}
            </div>
            {children}
        </div>
    );
}
